import { AuthController } from "@somewear/auth";
import { useEffect, useRef, useState } from "react";

enum Phase {
	Idle = "idle",
	SigningOut = "signingOut",
	AwaitingAuth = "awaitingAuth",
	Done = "done",
}

// Shared state machine for URL-triggered auth bounce flows (external auth, docs handoff, ticket auth).
// getTarget reads the URL once on mount — returning null opts out of the flow entirely.
export function useAuthSessionBounce<Target>(
	getTarget: () => Target | null,
	forceSignOut: boolean,
	authServiceLoaded: boolean,
	hasSession: boolean | undefined,
	activeAuthId: string | undefined,
	onToken: (tokenString: string, target: Target) => void,
): boolean {
	const [redirecting, setRedirecting] = useState(false);
	const [target] = useState(getTarget);
	const phase = useRef<Phase>(Phase.Idle);

	useEffect(() => {
		if (!target || !authServiceLoaded || hasSession === undefined) return;

		switch (phase.current) {
			case Phase.Idle:
				if (forceSignOut && hasSession) {
					if (activeAuthId === undefined) return;
					phase.current = Phase.SigningOut;
					AuthController.service.signOut$().subscribe();
				} else {
					phase.current = Phase.AwaitingAuth;
				}
				return;

			case Phase.SigningOut:
				if (activeAuthId !== undefined) return;
				phase.current = Phase.AwaitingAuth;
				return;

			case Phase.AwaitingAuth:
				if (activeAuthId === undefined) return;
				break;

			case Phase.Done:
				return;
		}

		setRedirecting(true);
		AuthController.service.getTokenString$().subscribe((tokenString) => {
			if (tokenString) {
				phase.current = Phase.Done;
				onToken(tokenString, target);
			} else {
				setRedirecting(false);
			}
		});
	}, [target, forceSignOut, authServiceLoaded, hasSession, activeAuthId, onToken]);

	return redirecting;
}
